import { assign } from 'xstate';
import type { MachineConfig } from 'xstate';
import type { UiContext } from './context';
import {
	assignConceptNameByActivation,
	assignConceptNameByDetails,
	assignPersonaName
} from './events';
import type { UiEvent } from './events';
import type { UiStateSchema } from './schema';

const config: MachineConfig<UiContext, UiStateSchema, UiEvent> = {
	id: 'game',
	initial: 'intro',
	states: {
		intro: {
			initial: 'player',
			states: {
				player: {
					entry: [
						'blurBackground',
						'hiddenPersonas',
						'hiddenToolbars',
						'hiddenReactions'
					],
					exit: ['clearBackground'],
					on: {
						NEXT: 'start'
					}
				},
				start: {
					entry: [
						'blurBackground',
						'hiddenPersonas',
						'hiddenToolbars',
						'hiddenReactions'
					],
					exit: ['clearBackground'],
					on: {
						CLOSE: 'end',
						DETAILS: 'why',
						NEXT: 'council'
					}
				},
				why: {
					entry: [
						'blurBackground',
						'hiddenPersonas',
						'hiddenToolbars',
						'hiddenReactions'
					],
					exit: ['clearBackground'],
					on: {
						CLOSE: 'end',
						NEXT: 'council'
					}
				},
				council: {
					entry: [
						'hiddenPersonas',
						'mapFog',
						'hiddenToolbars',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearFog', 'hiddenTutorial'],
					on: {
						NEXT: 'personas',
						SKIP_TUTORIAL: 'end'
					}
				},
				personas: {
					entry: [
						assign<UiContext, UiEvent>({
							personasVisible: true,
							personasEnabled: true,
							personasEnabledIndex: 0
						}),
						'mapFog',
						'hiddenToolbars',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearFog', 'hiddenTutorial'],
					on: {
						PERSONA_DETAILS: {
							target: 'persona',
							actions: assignPersonaName
						},
						SKIP_TUTORIAL: 'end'
					}
				},
				persona: {
					entry: [
						'blurBackground',
						'mapFog',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearBackground', 'clearFog', 'hiddenTutorial'],
					on: {
						CLOSE: 'score',
						SKIP_TUTORIAL: 'end'
					}
				},
				score: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: false,
							toolbarConceptsEnabled: false
						}),
						'personasFog',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearFog', 'hiddenTutorial'],
					on: {
						NEXT: 'actions',
						SKIP_TUTORIAL: 'end'
					}
				},
				actions: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: true
						}),
						'personasFog',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearFog', 'hiddenTutorial'],
					on: {
						CONCEPTS: 'concepts',
						SKIP_TUTORIAL: 'end'
					}
				},
				concepts: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'blurBackground',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearBackground', 'hiddenTutorial'],
					on: {
						CLOSE: 'actions',
						ACTIVATE: 'effect',
						DETAILS: 'concept',
						SKIP_TUTORIAL: 'end'
					}
				},
				concept: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'blurBackground',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearBackground', 'hiddenTutorial'],
					on: {
						CLOSE: 'concepts',
						ACTIVATE: 'effect',
						SKIP_TUTORIAL: 'end'
					}
				},
				effect: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'blurBackground',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearBackground', 'hiddenTutorial'],
					on: {
						NEXT: 'result',
						SKIP_TUTORIAL: 'end'
					}
				},
				result: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'blurBackground',
						'disabledPersonas',
						'hiddenReactions',
						'visibleTutorial'
					],
					exit: ['clearBackground', 'hiddenTutorial'],
					on: {
						NEXT: 'feedback',
						SKIP_TUTORIAL: 'end'
					}
				},
				feedback: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'disabledPersonas',
						'visibleReactions',
						'visibleTutorial'
					],
					exit: ['hiddenTutorial'],
					on: {
						NEXT: 'summary',
						SKIP_TUTORIAL: 'end'
					}
				},
				summary: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'personasFog',
						'disabledPersonas',
						'visibleReactions',
						'visibleTutorial'
					],
					exit: ['clearFog', 'hiddenTutorial'],
					on: {
						NEXT: 'season',
						SKIP_TUTORIAL: 'end'
					}
				},
				season: {
					entry: [
						assign<UiContext, UiEvent>({
							toolbarScoreVisible: true,
							toolbarConceptsVisible: true,
							toolbarConceptsEnabled: false
						}),
						'personasFog',
						'disabledPersonas',
						'visibleReactions',
						'visibleTutorial'
					],
					exit: ['clearFog', 'hiddenTutorial'],
					on: {
						NEXT: 'end',
						SKIP_TUTORIAL: 'end'
					}
				},
				end: {
					type: 'final'
				}
			},
			onDone: 'overview'
		},
		overview: {
			entry: ['enabledPersonas', 'enabledToolbars', 'visibleReactions'],
			on: {
				PERSONA_DETAILS: {
					target: 'persona',
					actions: assignPersonaName
				},
				CONCEPTS: 'concepts',
				FINISH_PLAYTHROUGH: [
					{
						target: 'outro.summary',
						cond: (_, event) => !event.wantFeedback
					},
					{
						target: 'outro'
					}
				]
			}
		},
		persona: {
			entry: ['blurBackground'],
			exit: ['clearBackground'],
			on: {
				CLOSE: 'overview'
			}
		},
		concepts: {
			initial: 'overview',
			states: {
				overview: {
					entry: ['blurBackground'],
					exit: ['clearBackground'],
					on: {
						CLOSE: 'back',
						CONCEPT_ACTIVATE: {
							target: 'effect',
							actions: assignConceptNameByActivation
						},
						CONCEPT_DETAILS: {
							target: 'details',
							actions: assignConceptNameByDetails
						}
					}
				},
				details: {
					entry: ['blurBackground'],
					exit: ['clearBackground'],
					on: {
						CLOSE: 'overview',
						CONCEPT_ACTIVATE: {
							target: 'effect',
							actions: assignConceptNameByActivation
						}
					}
				},
				effect: {
					entry: ['blurBackground', 'hiddenReactions'],
					exit: ['clearBackground'],
					on: {
						NEXT: {
							target: 'result'
						}
					}
				},
				result: {
					entry: ['blurBackground', 'hiddenReactions'],
					exit: ['clearBackground'],
					on: {
						NEXT: 'combination',
						CLOSE: 'back'
					}
				},
				combination: {
					entry: ['blurBackground', 'hiddenReactions'],
					exit: ['clearBackground'],
					on: {
						CLOSE: 'back'
					}
				},
				back: {
					type: 'final'
				}
			},
			onDone: 'overview'
		},
		outro: {
			initial: 'feedback',
			states: {
				feedback: {
					entry: ['blurBackground'],
					exit: ['clearBackground'],
					on: {
						NEXT: 'summary'
					}
				},
				summary: {
					entry: ['blurBackground'],
					exit: ['clearBackground'],
					on: {
						NEW: '#game.overview',
						FINISH: 'end'
					}
				},
				end: {
					type: 'final'
				}
			},
			onDone: 'intro'
		}
	}
};

export default config;
